import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Chrome, Mail, Lock, User, Phone } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import PublicNavbar from "../../components/PublicNavbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientSignup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: ""
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (form.password !== form.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/client/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          password: form.password,
          phone: form.phone
        })
      });

      if (response.ok) {
        toast.success("Account created successfully!");
        navigate("/client/dashboard");
      } else {
        const err = await response.json();
        toast.error(err.detail || "Signup failed");
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    const url = new URL(`${API}/auth/customer/google`);
    url.searchParams.set("origin_url", window.location.origin);
    window.location.href = url.toString();
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] relative overflow-hidden">
      <PublicNavbar />

      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#3B82F6]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#3B82F6]/3 rounded-full blur-3xl"></div>
      </div>

      <section className="relative z-10 pt-24 pb-12 px-6 flex items-center justify-center min-h-[calc(100vh-80px)]">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-[#FAFAFA] mb-2">Create Account</h1>
            <p className="text-[#A1A1AA]">Join DopeInHD as a client</p>
          </div>

          <div className="bg-[#121214] border border-[#27272A] rounded-xl p-8">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-[#A1A1AA]">Full Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={18} />
                  <Input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    className="pl-10 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                    placeholder="John Doe"
                    data-testid="client-signup-name"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#A1A1AA]">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={18} />
                  <Input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    className="pl-10 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                    placeholder="you@example.com"
                    data-testid="client-signup-email"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#A1A1AA]">Phone (Optional)</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={18} />
                  <Input
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange}
                    className="pl-10 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                    placeholder="+1 (555) 000-0000"
                    data-testid="client-signup-phone"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#A1A1AA]">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={18} />
                  <Input
                    name="password"
                    type="password"
                    value={form.password}
                    onChange={handleChange}
                    required
                    className="pl-10 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                    placeholder="••••••••"
                    data-testid="client-signup-password"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#A1A1AA]">Confirm Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={18} />
                  <Input
                    name="confirmPassword"
                    type="password"
                    value={form.confirmPassword}
                    onChange={handleChange}
                    required
                    className="pl-10 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                    placeholder="••••••••"
                    data-testid="client-signup-confirm"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#3B82F6] text-white font-semibold hover:bg-[#2563EB]"
                data-testid="client-signup-submit"
              >
                {loading ? "Creating Account..." : "Create Account"}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#27272A]"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-[#121214] px-2 text-[#71717A]">or continue with</span>
              </div>
            </div>

            <Button
              onClick={handleGoogleSignup}
              variant="outline"
              className="w-full border-[#27272A] text-[#FAFAFA] hover:bg-[#1A1A1D]"
              data-testid="client-google-signup"
            >
              <Chrome size={20} className="mr-2" />
              Sign up with Google
            </Button>

            <p className="mt-6 text-center text-sm text-[#A1A1AA]">
              Already have an account?{" "}
              <Link to="/client/login" className="text-[#3B82F6] hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ClientSignup;
